import { Injectable } from '@angular/core';
import { WEBSOCKET_URL } from '../../config';
 
@Injectable({ providedIn: 'root' })
export class ChatStreamRelayWebSocketService {
  private ws: WebSocket | null = null;
  private pingInterval: any = null;
  private pongTimeout: any = null;
  private chatId: string = '';
  private onMessageCallback: ((data: string) => void) | null = null;
 
  // Send a ping every 20 seconds
  private readonly PING_INTERVAL_MS = 20000;
  // If no pong received within 10 seconds, reconnect immediately
  private readonly PONG_TIMEOUT_MS = 10000;
 
  connect(chatId: string, onMessage: (data: string) => void, onClose?: () => void) {
    // Store for reconnection
    this.chatId = chatId;
    this.onMessageCallback = onMessage;
   
    // Clean up any existing connection first
    this.cleanup();
   
    console.log('[WebSocket] Connecting with chatId:', chatId);
    const wsUrl = `${WEBSOCKET_URL}?chatId=${chatId}`;
    this.ws = new WebSocket(wsUrl);
   
    this.ws.onopen = () => {
      console.log('[WebSocket] Connection opened');
      this.startPingInterval();
    };
   
    this.ws.onmessage = (event) => {
      // Any message received means connection is alive - clear pong timeout
      this.clearPongTimeout();
     
      // Filter out ping/pong messages from being passed to the app
      if (event.data === 'pong' || event.data === 'ping') {
        return;
      }
      if (this.onMessageCallback) {
        this.onMessageCallback(event.data);
      }
    };
   
    this.ws.onerror = (error) => {
      console.error('[WebSocket] Error:', error);
      this.reconnect();
    };
   
    this.ws.onclose = (event) => {
      console.warn('[WebSocket] Connection closed. Code:', event.code);
      this.stopPingInterval();
      // Auto-reconnect on any close
      this.reconnect();
    };
  }
 
  private reconnect() {
    // Wait 2 seconds before reconnecting
    setTimeout(() => {
      if (this.chatId && this.onMessageCallback) {
        console.log('[WebSocket] Reconnecting...');
        this.connect(this.chatId, this.onMessageCallback);
      }
    }, 2000);
  }
 
  private startPingInterval() {
    this.stopPingInterval();
   
    this.pingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send('ping');
       
        // Set timeout - if no pong, reconnect
        this.pongTimeout = setTimeout(() => {
          console.warn('[WebSocket] No pong received, reconnecting...');
          this.cleanup();
          this.reconnect();
        }, this.PONG_TIMEOUT_MS);
      }
    }, this.PING_INTERVAL_MS);
  }
 
  private stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    this.clearPongTimeout();
  }
 
  private clearPongTimeout() {
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }
 
  private cleanup() {
    this.stopPingInterval();
    if (this.ws) {
      this.ws.onclose = null; // Prevent onclose from triggering reconnect
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }
  }
 
  send(data: string) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    } else {
      console.warn('[WebSocket] Cannot send - connection not open');
    }
  }
 
  close() {
    this.chatId = '';
    this.onMessageCallback = null;
    this.cleanup();
  }
 
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}